import { computed, MaybeRef, unref } from 'vue'
import { JobRateCardBandQuery, RateCardNoMatchReason, RateType } from '@/gql/graphql'
import useDate from '@/composables/useDate'
import { $t } from '@/composables/translation'
import { durationRateTypeUnitLabels } from '@/components/Job/functions/durationRateTypeUnitLabels'
import { formatRateCardAmountRange } from '@/components/Job/composables/rateCardBandRangeText'
import {
  silentRateCardNoMatchReasons,
  useRateCardNoMatchReasonCopy,
} from '@/components/Job/composables/rateCardNoMatchReasonCopy'

export type RateCardCriterion = { label: string; value: string }

type RawRateCardBand = NonNullable<NonNullable<JobRateCardBandQuery['job']>['rateCardBand']>

export type RateCardBand =
  | (Omit<RawRateCardBand, 'criteria' | 'validUntil'> & {
      validUntil?: string | null
      criteria?: RateCardCriterion[] | null
    })
  | null

export type RateCardGuidance =
  | {
      hasMatch: true
      introText: string
      rangeAmountText: string
      unitText: string | null
      excludesMspFee: boolean
      validUntilText: string | null
      criteria: RateCardCriterion[]
      tooltipText: string
    }
  | {
      hasMatch: false
      reasonTitle: string
      reasonDescription: string
    }

/**
 * Shared by useRateCardBandGuidance and useSupplierRateCardBandGuidance — both read the same
 * band shape and need the same bolded-amount, unit and validUntil text.
 */
export function useRateCardGuidanceBandFields(
  band: MaybeRef<RateCardBand>,
  currencySymbol: MaybeRef<string>,
) {
  const bandRef = computed(() => unref(band))

  const rangeAmountText = computed(() => {
    const { value } = bandRef
    if (value?.minimum == null || value?.maximum == null) return ''

    return formatRateCardAmountRange(value.minimum, value.maximum, unref(currencySymbol), ' – ')
  })

  const unitText = computed(() => {
    const label = durationRateTypeUnitLabels()[bandRef.value?.rateType as RateType]
    return label ? $t('per :unit', { unit: label }) : null
  })

  const { date: formattedValidUntil } = useDate(computed(() => bandRef.value?.validUntil ?? ''))

  const validUntilText = computed(() =>
    bandRef.value?.validUntil ? $t('Valid until :date', { date: formattedValidUntil.value }) : null,
  )

  return { rangeAmountText, unitText, validUntilText }
}

/** Shared no-match branch — identical for the direct-client and supplier-leg composables. */
export function useNoMatchGuidance() {
  const noMatchReasonCopy = useRateCardNoMatchReasonCopy()

  return (reason: RateCardNoMatchReason | null | undefined): RateCardGuidance | undefined => {
    if (!reason || silentRateCardNoMatchReasons.has(reason)) return undefined

    return {
      hasMatch: false,
      reasonTitle: $t('No rate card match'),
      reasonDescription: noMatchReasonCopy.value[reason],
    }
  }
}

/**
 * The backend comparison this gates on is debounced, so it can still read AboveMaximum for a
 * moment after the rate is corrected back under the maximum — re-check the live value here so
 * that moment never produces a negative "above maximum" amount.
 */
export function aboveMaximumMessage(
  entered: string | number | undefined | null,
  maximum: number | string | null | undefined,
  currencySymbol: string,
): string | undefined {
  if (entered == null || entered === '') return undefined

  const value = Number(entered)
  if (value === 0 || maximum == null) return undefined

  const max = Number(maximum)
  if (value <= max) return undefined

  return $t(':amount above the rate card maximum of :max', {
    amount: `${currencySymbol} ${(value - max).toFixed(2)}`,
    max: `${currencySymbol} ${max.toFixed(2)}`,
  })
}

export function rateCardTooltipText(isClientBand: boolean): string {
  return isClientBand
    ? $t(
        'A rate card sets agreed pay ranges for certain jobs. Your client may have several, and the job details below decide which one applies, if any.',
      )
    : $t(
        'A rate card sets agreed pay ranges for certain jobs. Your company may have several, and the job details below decide which one applies, if any.',
      )
}
